import { WarrantyPart } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { WarrantyPartsTotalPrice, useWarrantyClaimContext } from '@components';
import { useAuth } from '@hooks';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Divider, IconButton, Stack, TextField } from '@mui/material';
import { t } from '@translations';
import { calculateTotalPrice } from '@utils/price.utils';
import { usePartsManager } from '../WarrantyCreate/usePartsManager';
import { WarrantySearchPart } from './WarrantySearchPart';

type WarrantyPartFieldsProps = {
  fields: WarrantyPart[];
  removePart: (index: number) => void;
};
export const WarrantyPartFields = ({ fields, removePart }: WarrantyPartFieldsProps) => {
  const { parts } = usePartsManager();
  const { form, isReadOnly } = useWarrantyClaimContext();

  const partsError = form.formState.errors?.parts;
  const { isBeremaUser } = useAuth();

  const quantityOnChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const value = e.target.value;
    const price = form.getValues(`parts.${index}.price`);
    const maxQuantity = form.getValues(`parts.${index}.maxQuantity`);
    const totalPrice = calculateTotalPrice(Number(value), Number(price));
    form.setValue(`parts.${index}.totalPrice`, totalPrice || undefined, { shouldDirty: true });
    if (Number(value) > Number(maxQuantity)) {
      form.setError(`parts.${index}.quantity`, {
        type: 'manual',
        message: t(TextCodes.Warranty.Block.QuantityErrorMax),
      });
    } else {
      form.clearErrors(`parts.${index}.quantity`);
    }
  };

  const priceOnChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const value = e.target.value;
    const quantity = form.getValues(`parts.${index}.quantity`);
    const totalPrice = calculateTotalPrice(Number(value), quantity);
    form.setValue(`parts.${index}.totalPrice`, totalPrice || undefined, { shouldDirty: true });
  };

  return (
    <Stack direction='column' spacing={2} sx={{ mt: 4 }}>
      {fields.length === 0 ? (
        <Box fontStyle='italic' color='text.secondary'>
          {t(TextCodes.Warranty.Block.NoItemsAdded)}
        </Box>
      ) : (
        fields.map((row, index) => {
          const isPartPartNoError = !!partsError?.[index]?.partNo;
          const isQuantityError = !!partsError?.[index]?.quantity;
          return (
            <Box
              key={row.id}
              display='flex'
              flexDirection={{
                xs: 'column',
                sm: 'row',
              }}
              alignItems='center'
            >
              <Box
                sx={{
                  width: {
                    xs: '100%',
                    sm: 'auto',
                  },
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 4, sm: 2 }}
                  width={{ xs: '100%', sm: 'auto' }}
                >
                  <WarrantySearchPart partRowIndex={index} />
                  <TextField
                    label={t(TextCodes.Warranty.Block.Description)}
                    {...form.register(`parts.${index}.designation`, { required: true })}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      '& label': {
                        display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                      },
                    }}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                    error={!!partsError?.[index]?.designation}
                  />
                  <TextField
                    label={t(TextCodes.Warranty.Block.Price)}
                    {...form.register(`parts.${index}.price`, {
                      required: true,
                      valueAsNumber: true,
                    })}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      '& label': {
                        display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                      },
                    }}
                    onChange={(e) => priceOnChange(e, index)}
                    type='number'
                    inputProps={{
                      readOnly: !isBeremaUser,
                      min: 0.0,
                      inputMode: 'decimal',
                      step: 0.01,
                    }}
                    error={!!partsError?.[index]?.price}
                  />
                  <TextField
                    label={t(TextCodes.Warranty.Block.Quantity)}
                    {...form.register(`parts.${index}.quantity`, {
                      required: true,
                      min: 1,
                      max: parts?.[index]?.maxQuantity,
                      valueAsNumber: true,
                    })}
                    onChange={(e) => quantityOnChange(e, index)}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      '& label': {
                        display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                      },
                    }}
                    type='number'
                    inputProps={{
                      min: 1,
                    }}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                    error={!!partsError?.[index]?.quantity}
                  />
                  <TextField
                    label={t(TextCodes.Warranty.Block.TotalPrice)}
                    {...form.register(`parts.${index}.totalPrice`)}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      '& label': {
                        display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                      },
                    }}
                    inputProps={{
                      readOnly: true,
                    }}
                  />
                </Stack>
                {isQuantityError && parts?.[index]?.maxQuantity ? (
                  <Box color='error.main' mt={1}>
                    {t(TextCodes.Warranty.Block.QuantityError, {
                      count: parts?.[index]?.maxQuantity?.toString() || '',
                    })}
                  </Box>
                ) : null}
                {isPartPartNoError && (
                  <Box color='error.main' mt={1}>
                    {t(TextCodes.Warranty.Block.PartCannotBeAdded)}
                  </Box>
                )}
              </Box>

              {!isReadOnly && (
                <IconButton
                  onClick={() => removePart(index)}
                  color='error'
                  sx={{
                    alignSelf: { xs: 'center', sm: 'baseline' },
                    ml: { xs: 0, sm: 2 },
                    my: { xs: 2, sm: 0 },
                  }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              )}
              <Divider
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  width: '100%',
                  mb: 2,
                }}
              />
            </Box>
          );
        })
      )}
      <WarrantyPartsTotalPrice fields={fields} />
    </Stack>
  );
};
